import { blendGlyphWeights, composeGlyphSequenceBinaryColumns, composeGlyphSequenceWeights, } from "./atlas.js";
import { evidenceAt } from "./preprocess.js";
import { rerankCandidates, rerankCandidatesEndingAtX } from "./rerank.js";
const TOKEN_SCORE = 1;
const TOKEN_FIT_TOLERANCE = 0.001;
export function compileDecodeToken(atlas, anchorText, options = {}) {
    let anchorKey = "";
    const glyphs = [];
    for (const ch of anchorText) {
        const glyph = atlas.byChar.get(ch);
        if (!glyph)
            throw new Error(`token contains unavailable glyph: ${JSON.stringify(ch)}`);
        anchorKey += glyph.anchorKey;
        glyphs.push(glyph);
    }
    return {
        anchorKey,
        glyphs,
        text: options.text ?? anchorText,
        acceptOnAnchorMatch: options.acceptOnAnchorMatch,
        terminate: options.terminate,
        trailingOnly: options.trailingOnly,
        trailingTolerance: options.trailingTolerance,
        minShapeScore: options.minShapeScore,
    };
}
/** Return exact-anchor-compatible glyphs at a position.
 *
 * Each input column is encoded as one UTF-16 code unit. Matching a glyph is just
 * a substring lookup in a width-keyed map. This is exact equality: extra exact
 * anchor pixels inside the glyph window are not allowed.
 */
export function anchorCandidatesAtX(atlas, inputAnchorString, x, endX) {
    const out = [];
    for (const width of atlas.widths) {
        if (endX !== undefined && x + width > endX)
            continue;
        if (x + width > inputAnchorString.length)
            continue;
        const key = inputAnchorString.substring(x, x + width);
        const found = atlas.lookupByWidth.get(width)?.get(key);
        if (found)
            out.push(...found);
    }
    return out;
}
/** Return whether a glyph is binary-mask-compatible at a left-to-right beam boundary.
 *
 * The candidate's logical body is composed with the previous glyph's right
 * overhang using binary OR, then compared exactly with the raw non-white input
 * mask. The candidate's own overhang is checked as a subset of observed ink:
 * it may overlap the following glyph, but it may not predict ink on white
 * background.
 */
export function glyphBinaryMaskMatchesAt(line, x, glyph, previous) {
    const previousOverhangWidth = previous ? previous.visualWidth - previous.width : 0;
    // (previous.overhang OR candidate.main) XOR input must be zero across the
    // candidate's complete logical advance.
    for (let dx = 0; dx < glyph.width; dx++) {
        const previousColumn = previous !== undefined && dx < previousOverhangWidth
            ? previous.binaryColumns[previous.width + dx] ?? 0
            : 0;
        const expected = (glyph.binaryColumns[dx] ?? 0) | previousColumn;
        if (expected !== (line.nonWhiteColumns[x + dx] ?? 0))
            return false;
    }
    // candidate.overhang AND (NOT input) must be zero. Extra observed ink is
    // allowed because the following glyph may start in these shared columns.
    for (let dx = glyph.width; dx < glyph.visualWidth; dx++) {
        const expected = glyph.binaryColumns[dx] ?? 0;
        const observed = line.nonWhiteColumns[x + dx] ?? 0;
        if ((expected & ~observed) !== 0)
            return false;
    }
    return true;
}
/** Match a complete multi-glyph token against the clean non-white input mask.
 *
 * The previous glyph's overhang is OR-composed into the token's first logical
 * columns. Every logical token column must match exactly. The final overhang is
 * a subset check for non-terminal tokens and an exact check for terminal ones.
 */
export function glyphSequenceBinaryMaskMatchesAt(line, x, glyphs, options = {}) {
    if (glyphs.length === 0)
        return false;
    const rendered = composeGlyphSequenceBinaryColumns(glyphs);
    if (x < 0 || x + rendered.visualWidth > line.width)
        return false;
    const columns = rendered.columns.slice();
    const previous = options.previous;
    if (previous) {
        const overhangWidth = previous.visualWidth - previous.width;
        for (let dx = 0; dx < overhangWidth && dx < columns.length; dx++) {
            columns[dx] = (columns[dx] ?? 0) | (previous.binaryColumns[previous.width + dx] ?? 0);
        }
    }
    for (let dx = 0; dx < rendered.logicalWidth; dx++) {
        if ((columns[dx] ?? 0) !== (line.nonWhiteColumns[x + dx] ?? 0))
            return false;
    }
    for (let dx = rendered.logicalWidth; dx < rendered.visualWidth; dx++) {
        const expected = columns[dx] ?? 0;
        const observed = line.nonWhiteColumns[x + dx] ?? 0;
        if (options.requireExactFinalOverhang) {
            if (expected !== observed)
                return false;
        }
        else if ((expected & ~observed) !== 0) {
            return false;
        }
    }
    return true;
}
/** Exact-anchor candidates pruned by composed binary-mask compatibility. */
export function binaryMaskCandidatesAtX(atlas, line, x, previous, endX) {
    return anchorCandidatesAtX(atlas, line.anchorString, x, endX)
        .filter((glyph) => glyphBinaryMaskMatchesAt(line, x, glyph, previous));
}
/** Return whether the observed line contains ink at one of this glyph's own ink pixels.
 *
 * Exact-anchor lookup can produce anchorless glyphs from an entirely blank
 * window. Requiring an overlap between the atlas ink mask and raw non-white
 * input evidence prevents blank padding from being emitted as a visible glyph
 * while preserving spaces, side bearings, and overhang outside the glyph's
 * ink shape.
 */
export function glyphHasInkEvidenceAt(line, x, glyph) {
    // A space is a validated blank advance, not a visible glyph.
    if (glyph.char === ' ')
        return true;
    for (let dx = 0; dx < glyph.width; dx++) {
        if (((glyph.binaryColumns[dx] ?? 0) & (line.nonWhiteColumns[x + dx] ?? 0)) !== 0)
            return true;
    }
    return false;
}
/** Exact-anchor candidate generation followed by midpoint diff reranking. */
export function rankedAtX(atlas, line, x, endX, confidenceFloor = 0.25) {
    const candidates = anchorCandidatesAtX(atlas, line.anchorString, x, endX)
        .filter((glyph) => glyphHasInkEvidenceAt(line, x, glyph));
    return rerankCandidates(line, x, candidates, confidenceFloor);
}
/** Return exact-anchor-compatible glyphs whose logical right edge is `endX`. */
export function anchorCandidatesEndingAtX(atlas, inputAnchorString, endX, startX = 0) {
    const out = [];
    for (const width of atlas.widths) {
        const x = endX - width;
        if (x < startX || x < 0 || endX > inputAnchorString.length)
            continue;
        const key = inputAnchorString.substring(x, endX);
        const found = atlas.lookupByWidth.get(width)?.get(key);
        if (found)
            out.push(...found);
    }
    return out;
}
/** Exact-anchor candidate generation and soft-evidence reranking at a right edge. */
export function rankedEndingAtX(atlas, line, endX, startX = 0, confidenceFloor = 0.25) {
    const candidates = anchorCandidatesEndingAtX(atlas, line.anchorString, endX, startX)
        .filter((glyph) => glyphHasInkEvidenceAt(line, endX - glyph.width, glyph));
    return rerankCandidatesEndingAtX(line, endX, candidates, confidenceFloor);
}
/** Decode a line body left-to-right with a small beam search.
 *
 * Only the starting x coordinate is required. The end x coordinate defaults to
 * the rightmost exact-cyan anchor column plus a small pad computed during input
 * preprocessing. At each boundary, exact anchor matching generates candidates;
 * midpoint pairwise scoring ranks them; the beam keeps the best partial paths.
 */
export function decodeLine(atlas, line, options) {
    const startX = options.startX;
    const endX = options.endX ?? line.estimatedEndX;
    const beamSize = options.beamSize ?? 32;
    const branchLimit = options.branchLimit ?? 8;
    const tokenList = options.tokenList ?? [];
    const denylist = new Set(options.denylist ?? "");
    const useOverhangEvidence = options.useOverhangEvidence ?? true;
    const useBinaryMaskPruning = line.profile.name === 'black' && (options.useBinaryMaskPruning ?? true);
    // Once binary masks agree exactly, every surviving disagreement is about
    // grayscale intensity. Do not suppress weak anti-alias pixels a second time.
    const confidenceFloor = options.confidenceFloor ?? (useBinaryMaskPruning ? 1 : 0.25);
    let beams = [{ text: "", x: startX, score: 0, decisions: [] }];
    let bestFinished;
    // Binary-mask compatibility depends on the previous glyph's overhang, so the
    // cache key must include both the cursor position and previous glyph.
    const rankCache = new Map();
    const getRanked = (x, previous) => {
        const cacheKey = `${x}:${previous?.orderIndex ?? -1}`;
        let ranked = rankCache.get(cacheKey);
        if (!ranked) {
            const candidates = (useBinaryMaskPruning
                ? binaryMaskCandidatesAtX(atlas, line, x, previous, endX)
                : anchorCandidatesAtX(atlas, line.anchorString, x, endX)
                    .filter((glyph) => glyphHasInkEvidenceAt(line, x, glyph)))
                .filter((glyph) => !denylist.has(glyph.char));
            ranked = rerankCandidates(line, x, candidates, confidenceFloor);
            rankCache.set(cacheKey, ranked);
        }
        return ranked;
    };
    const canFinish = (beam) => {
        if (!beam.text || !beam.lastGlyph)
            return false;
        const visualEndX = beam.x + (beam.lastGlyph.visualWidth - beam.lastGlyph.width);
        return beam.x >= line.exactX1 && visualEndX >= line.inkX1;
    };
    const finish = (beam) => {
        if (!canFinish(beam))
            return;
        const finished = {
            ...beam,
            score: beam.score + (useOverhangEvidence ? overhangFitImprovement(line, beam.x, beam.lastGlyph) : 0),
        };
        if (!bestFinished
            || finished.score > bestFinished.score
            || (finished.score === bestFinished.score && finished.x > bestFinished.x)) {
            bestFinished = finished;
        }
    };
    for (let step = 0; step < 256; step++) {
        if (beams.length === 0)
            break;
        const next = [];
        for (const beam of beams) {
            if (beam.x === endX) {
                finish(beam);
                continue;
            }
            if (beam.x > endX)
                continue;
            const tokenDecision = firstTokenDecision(tokenList, atlas, line, beam, endX, denylist, useOverhangEvidence, useBinaryMaskPruning);
            if (tokenDecision) {
                const nextBeam = {
                    text: beam.text + tokenDecision.selected.token.text,
                    x: beam.x + tokenDecision.selected.token.anchorKey.length,
                    score: beam.score + tokenDecision.selected.pairwiseScore,
                    decisions: [...beam.decisions, tokenDecision],
                    lastGlyph: tokenDecision.selected.token.glyphs.at(-1) ?? beam.lastGlyph,
                };
                if (tokenDecision.selected.token.terminate) {
                    const tokenVisualEndX = nextBeam.x
                        + finalOverhangWidth(tokenDecision.selected.token.glyphs);
                    if (useOverhangEvidence && tokenVisualEndX >= line.inkX1) {
                        nextBeam.score += overhangFitImprovement(line, nextBeam.x, nextBeam.lastGlyph);
                    }
                    return publicDecodeResult(nextBeam);
                }
                if (nextBeam.x <= endX)
                    next.push(nextBeam);
                continue;
            }
            const ranked = getRanked(beam.x, beam.lastGlyph);
            if (ranked.length === 0) {
                // A terminal overhang may extend beyond the logical cursor. It explains
                // the remaining edge pixels without introducing another character.
                finish(beam);
                continue;
            }
            for (const cand of ranked.slice(0, branchLimit)) {
                const second = ranked.length > 1 ? ranked[1] : undefined;
                const margin = second ? cand.pairwiseScore - second.pairwiseScore : 0;
                const decision = {
                    kind: "glyph",
                    step: beam.decisions.length,
                    x: beam.x,
                    selected: cand,
                    second,
                    candidateCount: ranked.length,
                    topCandidates: ranked.slice(0, Math.min(8, ranked.length)),
                    margin,
                };
                next.push({
                    text: beam.text + cand.glyph.char,
                    x: beam.x + cand.glyph.width,
                    score: beam.score + cand.pairwiseScore
                        + (useOverhangEvidence ? overhangFitImprovement(line, beam.x, beam.lastGlyph, cand.glyph) : 0),
                    decisions: [...beam.decisions, decision],
                    lastGlyph: cand.glyph,
                });
            }
        }
        if (next.length === 0)
            break;
        next.sort((a, b) => {
            const aValid = a.x <= endX ? 1 : 0;
            const bValid = b.x <= endX ? 1 : 0;
            if (bValid !== aValid)
                return bValid - aValid;
            return b.score - a.score;
        });
        beams = next.slice(0, beamSize);
    }
    if (bestFinished)
        return publicDecodeResult(bestFinished);
    if (beams.length > 0) {
        const best = beams.reduce((previous, current) => (current.score > previous.score || (current.score === previous.score && current.x > previous.x) ? current : previous));
        return publicDecodeResult(best);
    }
    return { text: "", x: startX, score: Number.NEGATIVE_INFINITY, decisions: [] };
}
function glyphShapeScoreAt(line, x, glyph) {
    let expectedMass = 0;
    let observedMass = 0;
    let intersection = 0;
    for (let y = 0; y < line.height; y++) {
        for (let dx = 0; dx < glyph.width; dx++) {
            const expected = (glyph.weights[y * glyph.visualWidth + dx] ?? 0) / 255;
            const observed = evidenceAt(line, x + dx, y);
            expectedMass += expected;
            observedMass += observed;
            intersection += Math.min(expected, observed);
        }
    }
    if (expectedMass <= 0 || observedMass <= 0 || intersection <= 0)
        return 0;
    const precision = intersection / observedMass;
    const recall = intersection / expectedMass;
    return precision + recall > 0 ? (2 * precision * recall) / (precision + recall) : 0;
}
function hasExactAnchor(glyph) {
    for (let i = 0; i < glyph.anchorKey.length; i++) {
        if (glyph.anchorKey.charCodeAt(i) !== 0)
            return true;
    }
    return false;
}
/** Decode a right-aligned line from its fixed logical right edge.
 *
 * Every candidate glyph must end at the beam's current x coordinate. Accepted
 * glyphs are prepended and the beam moves left by the glyph advance. Decoding
 * finishes naturally when no exact-anchor-compatible glyph can end at the
 * current boundary; no left-edge detection is required.
 */
export function decodeLineRightToLeft(atlas, line, options) {
    const startX = options.startX ?? 0;
    const endX = options.endX;
    const beamSize = options.beamSize ?? 32;
    const branchLimit = options.branchLimit ?? 8;
    const confidenceFloor = options.confidenceFloor ?? 0.25;
    const minAnchorlessShapeScore = options.minAnchorlessShapeScore ?? 0.45;
    const denylist = new Set(options.denylist ?? "");
    const useOverhangEvidence = options.useOverhangEvidence ?? true;
    if (!Number.isInteger(endX) || endX < startX || endX > line.width) {
        throw new RangeError(`endX ${endX} is outside [${startX}, ${line.width}]`);
    }
    let beams = [{ text: "", x: endX, score: 0, decisions: [] }];
    const finished = [];
    const rankCache = new Map();
    const getRanked = (x) => {
        let ranked = rankCache.get(x);
        if (!ranked) {
            const candidates = anchorCandidatesEndingAtX(atlas, line.anchorString, x, startX)
                .filter((glyph) => !denylist.has(glyph.char))
                .filter((glyph) => glyphHasInkEvidenceAt(line, x - glyph.width, glyph))
                .filter((glyph) => hasExactAnchor(glyph)
                || glyphShapeScoreAt(line, x - glyph.width, glyph) >= minAnchorlessShapeScore);
            ranked = rerankCandidatesEndingAtX(line, x, candidates, confidenceFloor);
            rankCache.set(x, ranked);
        }
        return ranked;
    };
    for (let step = 0; step < 256 && beams.length > 0; step++) {
        const next = [];
        for (const beam of beams) {
            const ranked = getRanked(beam.x);
            if (ranked.length === 0) {
                if (beam.text)
                    finished.push(beam);
                continue;
            }
            for (const cand of ranked.slice(0, branchLimit)) {
                const x = beam.x - cand.glyph.width;
                if (x < startX)
                    continue;
                const second = ranked.length > 1 ? ranked[1] : undefined;
                const margin = second ? cand.pairwiseScore - second.pairwiseScore : 0;
                const decision = {
                    kind: "glyph",
                    step: beam.decisions.length,
                    x,
                    selected: cand,
                    second,
                    candidateCount: ranked.length,
                    topCandidates: ranked.slice(0, Math.min(8, ranked.length)),
                    margin,
                };
                const overhangScore = !useOverhangEvidence
                    ? 0
                    : beam.firstGlyph
                        ? overhangFitImprovement(line, beam.x, cand.glyph, beam.firstGlyph)
                        : overhangFitImprovement(line, endX, cand.glyph);
                next.push({
                    text: cand.glyph.char + beam.text,
                    x,
                    score: beam.score + cand.pairwiseScore + overhangScore,
                    decisions: [...beam.decisions, decision],
                    firstGlyph: cand.glyph,
                });
            }
        }
        next.sort((a, b) => {
            if (b.score !== a.score)
                return b.score - a.score;
            if (a.x !== b.x)
                return a.x - b.x;
            return b.text.length - a.text.length;
        });
        beams = next.slice(0, beamSize);
    }
    if (finished.length > 0) {
        return publicRightToLeftDecodeResult(finished.reduce((best, cur) => {
            if (cur.x !== best.x)
                return cur.x < best.x ? cur : best;
            if (cur.score !== best.score)
                return cur.score > best.score ? cur : best;
            return cur.text.length > best.text.length ? cur : best;
        }));
    }
    if (beams.length > 0) {
        return publicRightToLeftDecodeResult(beams.reduce((best, cur) => {
            if (cur.x !== best.x)
                return cur.x < best.x ? cur : best;
            return cur.score > best.score ? cur : best;
        }));
    }
    return { text: "", x: endX, score: Number.NEGATIVE_INFINITY, decisions: [] };
}
function firstTokenDecision(tokenList, atlas, line, beam, endX, denylist, useOverhangEvidence, useBinaryMaskPruning) {
    for (let priorityIndex = 0; priorityIndex < tokenList.length; priorityIndex++) {
        const token = tokenList[priorityIndex];
        if (!token)
            continue;
        if (!line.anchorString.startsWith(token.anchorKey, beam.x))
            continue;
        const tokenEndX = beam.x + token.anchorKey.length;
        const tokenVisualEndX = tokenEndX + finalOverhangWidth(token.glyphs);
        if (!token.terminate && tokenEndX > endX)
            continue;
        if (token.trailingOnly && Math.abs(tokenVisualEndX - line.inkX1) > (token.trailingTolerance ?? 0))
            continue;
        if (useBinaryMaskPruning && !glyphSequenceBinaryMaskMatchesAt(line, beam.x, token.glyphs, {
            previous: beam.lastGlyph,
            requireExactFinalOverhang: token.terminate === true,
        }))
            continue;
        const tokenShapeScore = glyphSequenceShapeScore(line, beam.x, token.glyphs);
        if (token.minShapeScore !== undefined && tokenShapeScore < token.minShapeScore)
            continue;
        const tokenScore = glyphSequenceFitScore(line, beam.x, token.glyphs, useOverhangEvidence);
        const precedingOverhangScore = useOverhangEvidence && token.glyphs.length > 0
            ? overhangFitImprovement(line, beam.x, beam.lastGlyph, token.glyphs[0])
            : 0;
        const glyphScore = token.acceptOnAnchorMatch
            ? undefined
            : bestGlyphOnlyFitScore(atlas, line, beam.x, tokenEndX, denylist, beam.lastGlyph, useOverhangEvidence, useBinaryMaskPruning);
        const combinedTokenScore = tokenScore + precedingOverhangScore;
        if (!token.acceptOnAnchorMatch && glyphScore !== undefined && combinedTokenScore + TOKEN_FIT_TOLERANCE < glyphScore)
            continue;
        const selected = {
            token,
            pairwiseScore: TOKEN_SCORE + combinedTokenScore + tokenShapeScore,
            wins: tokenList.length - priorityIndex,
            priorityIndex,
        };
        return {
            kind: "token",
            step: beam.decisions.length,
            x: beam.x,
            selected,
            candidateCount: 1,
            topCandidates: [selected],
            margin: glyphScore === undefined ? combinedTokenScore : combinedTokenScore - glyphScore,
        };
    }
    return undefined;
}
function publicDecodeResult(beam) {
    const { lastGlyph: _lastGlyph, ...result } = beam;
    return result;
}
function publicRightToLeftDecodeResult(beam) {
    const { firstGlyph: _firstGlyph, ...result } = beam;
    return result;
}
function finalOverhangWidth(glyphs) {
    const last = glyphs.at(-1);
    return last ? last.visualWidth - last.width : 0;
}
/**
 * Compare the complete composed soft glyph shape with the observed evidence.
 * Blank background pixels do not dominate the score.
 */
function glyphSequenceShapeScore(line, x, glyphs) {
    const rendered = composeGlyphSequenceWeights(glyphs);
    let expectedMass = 0;
    let observedMass = 0;
    let intersection = 0;
    for (let y = 0; y < line.height; y++) {
        for (let dx = 0; dx < rendered.visualWidth; dx++) {
            const expected = rendered.weights[y * rendered.visualWidth + dx] ?? 0;
            const observed = evidenceAt(line, x + dx, y);
            expectedMass += expected;
            observedMass += observed;
            intersection += Math.min(expected, observed);
        }
    }
    if (expectedMass <= 0 || observedMass <= 0 || intersection <= 0)
        return 0;
    const precision = intersection / observedMass;
    const recall = intersection / expectedMass;
    return precision + recall > 0 ? (2 * precision * recall) / (precision + recall) : 0;
}
function overhangFitDelta(line, x, previous, current) {
    if (!previous || previous.visualWidth === previous.width)
        return { improvement: 0, pixels: 0 };
    const overhangWidth = previous.visualWidth - previous.width;
    let improvement = 0;
    let pixels = 0;
    for (let y = 0; y < line.height; y++) {
        for (let dx = 0; dx < overhangWidth; dx++) {
            const overhang = (previous.weights[y * previous.visualWidth + previous.width + dx] ?? 0) / 255;
            if (overhang === 0)
                continue;
            const baseline = current && dx < current.width
                ? (current.weights[y * current.visualWidth + dx] ?? 0) / 255
                : 0;
            const composite = blendGlyphWeights(overhang, baseline);
            const actual = evidenceAt(line, x + dx, y);
            const baselineDiff = actual - baseline;
            const compositeDiff = actual - composite;
            improvement += baselineDiff * baselineDiff - compositeDiff * compositeDiff;
            pixels += 1;
        }
    }
    return { improvement, pixels };
}
/** Incremental fit gained by compositing a previous glyph's right overhang
 * with the next glyph, or with blank space at the end of a line. */
function overhangFitImprovement(line, x, previous, current) {
    const delta = overhangFitDelta(line, x, previous, current);
    return delta.pixels === 0 ? 0 : delta.improvement / delta.pixels;
}
function glyphSequenceFitScore(line, x, glyphs, useOverhangEvidence) {
    let error = 0;
    let pixels = 0;
    let previous;
    for (const glyph of glyphs) {
        const fit = glyphFitError(line, x, glyph);
        error += fit.error;
        pixels += fit.pixels;
        if (useOverhangEvidence)
            error -= overhangFitDelta(line, x, previous, glyph).improvement;
        previous = glyph;
        x += glyph.width;
    }
    return pixels === 0 ? Number.NEGATIVE_INFINITY : 1 - error / pixels;
}
function bestGlyphOnlyFitScore(atlas, line, startX, targetX, denylist, precedingGlyph, useOverhangEvidence, useBinaryMaskPruning) {
    let states = [{ x: startX, lastGlyph: precedingGlyph, error: 0, pixels: 0 }];
    for (let step = 0; step < 128 && states.length > 0; step++) {
        const nextByKey = new Map();
        let advanced = false;
        for (const state of states) {
            if (state.x === targetX) {
                const key = `${state.x}\0${state.lastGlyph?.orderIndex ?? -1}`;
                const existing = nextByKey.get(key);
                if (!existing || state.error / Math.max(1, state.pixels) < existing.error / Math.max(1, existing.pixels)) {
                    nextByKey.set(key, state);
                }
                continue;
            }
            const candidates = (useBinaryMaskPruning
                ? binaryMaskCandidatesAtX(atlas, line, state.x, state.lastGlyph, targetX)
                : anchorCandidatesAtX(atlas, line.anchorString, state.x, targetX)
                    .filter((glyph) => glyphHasInkEvidenceAt(line, state.x, glyph)))
                .filter((glyph) => !denylist.has(glyph.char));
            for (const glyph of candidates) {
                const nextX = state.x + glyph.width;
                if (nextX > targetX)
                    continue;
                const fit = glyphFitError(line, state.x, glyph);
                const improvement = useOverhangEvidence
                    ? overhangFitDelta(line, state.x, state.lastGlyph, glyph).improvement
                    : 0;
                const candidate = {
                    x: nextX,
                    lastGlyph: glyph,
                    error: state.error + fit.error - improvement,
                    pixels: state.pixels + fit.pixels,
                };
                const key = `${nextX}\0${glyph.orderIndex}`;
                const existing = nextByKey.get(key);
                if (!existing || candidate.error / Math.max(1, candidate.pixels) < existing.error / Math.max(1, existing.pixels)) {
                    nextByKey.set(key, candidate);
                }
                advanced = true;
            }
        }
        states = [...nextByKey.values()];
        if (!advanced || states.every((state) => state.x === targetX))
            break;
    }
    const finished = states.filter((state) => state.x === targetX && state.pixels > 0);
    if (finished.length === 0)
        return undefined;
    const best = finished.reduce((a, b) => (b.error / b.pixels < a.error / a.pixels ? b : a));
    return 1 - best.error / best.pixels;
}
function glyphFitError(line, x, glyph) {
    let error = 0;
    let pixels = 0;
    for (let y = 0; y < line.height; y++) {
        for (let dx = 0; dx < glyph.width; dx++) {
            const expected = (glyph.weights[y * glyph.visualWidth + dx] ?? 0) / 255;
            const actual = evidenceAt(line, x + dx, y);
            const diff = actual - expected;
            error += diff * diff;
            pixels += 1;
        }
    }
    return { error, pixels };
}
/** Build expected character positions for benchmark output. */
export function expectedLayout(atlas, text, startX) {
    const out = new Map();
    let x = startX;
    for (let index = 0; index < text.length; index++) {
        const ch = text[index];
        if (ch === undefined)
            continue;
        const glyph = atlas.byChar.get(ch);
        if (!glyph)
            throw new Error(`unsupported benchmark char: ${JSON.stringify(ch)}`);
        out.set(x, { index, char: ch, width: glyph.width });
        x += glyph.width;
    }
    return out;
}
//# sourceMappingURL=decoder.js.map